import { watch, type Ref, type WatchStopHandle } from 'vue'
import { PHONE_MIN_DIGITS } from '@/constants/phone-codes'
import { usePhoneNumberFormat } from '../format/usePhoneNumberFormat'
import { usePhoneCodeAutoDetect } from '../detect/usePhoneCodeAutoDetect'
import type { PhoneCode } from '@/constants/phone-codes'

/**
 * Composable для синхронізації номера телефону з props та автодетекту країни
 * Відповідає за: Single Responsibility - тільки синхронізація та автодетект
 */
export function usePhoneNumberSync(
    modelValue: Ref<string | undefined>,
    phoneNumber: Ref<string>,
    currentCountryIso: Ref<string>,
    setCode: (country: PhoneCode) => void,
    onUpdate: (value: string) => void,
    onComplete?: () => void,
    onCountryChanged?: () => void
) {
    const {
        parseModelValue,
        normalizeToDisplay,
        getCountryByIso,
    } = usePhoneNumberFormat()
    const { detectCountryByPhoneNumber } = usePhoneCodeAutoDetect()

    const canDetectCountry = (phoneValue: string): boolean => {
        const digits = (phoneValue ?? '').replace(/[^\d]/g, '')
        return digits.length >= 2
    }

    /** Нормалізує значення для детекту: з рядка цифр робить "+380501234567" */
    const valueForDetect = (phoneValue: string): string => {
        const trimmed = (phoneValue ?? '').trim()
        if (trimmed.startsWith('+')) return trimmed
        const digits = trimmed.replace(/\D/g, '')
        return digits ? `+${digits}` : ''
    }

    /**
     * Виконує автодетект країни по номеру телефону.
     * Працює і з рядком цифр без "+" — країна визначається по повному номеру.
     */
    const performAutoDetect = (phoneValue: string): void => {
        if (!canDetectCountry(phoneValue)) {
            setCode({} as PhoneCode)
            onCountryChanged?.()
            return
        }

        const toDetect = valueForDetect(phoneValue)
        const detectedCountry = toDetect ? detectCountryByPhoneNumber(toDetect) : null
        if (detectedCountry?.isoCode && detectedCountry.isoCode !== currentCountryIso.value) {
            setCode(detectedCountry)
            onCountryChanged?.()
        } else if (!detectedCountry && currentCountryIso.value) {
            setCode({} as PhoneCode)
            onCountryChanged?.()
        }
    }

    /**
     * Обробляє оновлення номера з props.modelValue
     */
    const handlePropsUpdate = (newValue: string | undefined): void => {
        const value = newValue ?? ''
        const digits = value.replace(/\D/g, '')
        if (!digits) {
            phoneNumber.value = ''
            setCode({} as PhoneCode)
            return
        }

        const parsed = parseModelValue(value)
        if (!parsed) {
            phoneNumber.value = digits ? `+${digits}` : ''
            setCode({} as PhoneCode)
            return
        }

        const normalized = normalizeToDisplay(value)
        phoneNumber.value = normalized

        const country = getCountryByIso(parsed.isoCode)
        if (country) {
            setCode(country)
        }

        const digitsOnly = (parsed.phoneCode + parsed.number).replace(/\D/g, '')
        if (digitsOnly && digitsOnly !== (modelValue.value ?? '').replace(/\D/g, '')) {
            onUpdate(digitsOnly)
        }

        if (normalized.length > PHONE_MIN_DIGITS + 1) {
            onComplete?.()
        }
    }

    /**
     * Синхронізує номер з props.modelValue
     * Парсить значення моделі та перетворює в нормалізований номер
     */
    const syncFromProps = (): WatchStopHandle => {
        return watch(
            () => modelValue.value,
            handlePropsUpdate,
            { immediate: true, flush: 'post' }
        )
    }

    /**
     * Синхронізує номер з phoneNumber та виконує автодетект країни.
     * Емітує тільки цифри (наприклад 380634050363) — країну можна визначити по коду.
     */
    const syncToModel = (): WatchStopHandle => {
        return watch(phoneNumber, (newValue) => {
            const digitsOnly = newValue?.replace(/\D/g, '') || ''
            if (digitsOnly !== (modelValue.value ?? '').replace(/\D/g, '')) {
                onUpdate(digitsOnly)
            }

            performAutoDetect(newValue)
        })
    }

    return {
        syncFromProps,
        syncToModel,
        /** Вызвать при изменении инпута — сразу определяет страну по введённым цифрам и обновляет код/флаг */
        syncCountryFromInput: performAutoDetect,
    }
}

